import type { PlaidAccount } from '@jcss/vue-plaid-link';
import { COUNTRY_CODE } from '../common/constants/constants';
import type {
  FundingSource,
  CheckfloProfile,
  CheckfloSubmerchantDto,
  FundingSourceListDto,
  CustomerStatus,
  AddressDto,
} from '../common/dtos';
import { AccountStatus } from '../common/enums/account-status';
import type { RegistrationFormValues, BankAdditionFormValues } from '../../base/components/PaperCheckFormModal.vue';
import type { CreatePlaidTokenRequest } from '../common/sign-up/bank/create-plaid-token-request';
import {
  CASH_OUT_ADDRESS_SAVE_ENDPOINT,
  CASH_OUT_ADDRESS_SAVE_RETRY_MESSAGE,
  readCashOutAddressFieldError,
} from '../utils/cash-out-address-error';

export interface BankSource {
  id: FundingSource['id'];
  $id: FundingSource['dwollaId'];
  fundingSourceUrl: FundingSource['dwollaLocation'];
  bankName: FundingSource['bankName'];
  customName: FundingSource['name'];
  bankAccountType: FundingSource['bankAccountType'];
  status: FundingSource['status'];
  forPayments: FundingSource['forPayments'];
}

interface DefaultPaymentMethod {
  id: number;
  type: string;
  fundingSourceId: number;
  addressId: number;
  shipping: string;
}

interface FundingSourcesState {
  connectedBanksCount: number;
  bankSources: BankSource[];
  rtpBankSources: BankSource[];
  wallet: FundingSource;
  fundingFromSystem: boolean;
  checkfloProfile: CheckfloProfile;
  checkfloSubmerchants: CheckfloSubmerchantDto[];
  companyLogo: string | null;
  microDeposit: string;
  publicPlaidToken: string | null;
  bankForFees: BankSource | null;
  bankForPayments: BankSource | null;
  loading: boolean;
  isFundingSourcesLoaded: boolean;
  selectedBankForFees: BankSource | null;
  defaultPaymentMethod: DefaultPaymentMethod | null;
}

export const convertFundingToBankSource = (fs: FundingSource): BankSource => ({
  id: fs.id,
  $id: fs.dwollaId,
  fundingSourceUrl: fs.dwollaLocation,
  bankName: fs.bankName,
  customName: fs.nickname ?? fs.name,
  bankAccountType: fs.bankAccountType,
  status: fs.status,
  forPayments: fs.forPayments,
});
import { defineStore } from 'pinia';
import { useMicroDepositStore } from './microdeposit';

let fundingSourcesRequest: Promise<void> | null = null;

export const useFundingSources = defineStore('fundingSources', {
  state: (): FundingSourcesState => ({
    connectedBanksCount: 0,
    bankSources: [],
    wallet: {} as FundingSource,
    fundingFromSystem: true,
    checkfloProfile: {} as CheckfloProfile,
    checkfloSubmerchants: [],
    companyLogo: null,
    microDeposit: '',
    publicPlaidToken: null,
    bankForFees: null,
    bankForPayments: null,
    loading: false,
    isFundingSourcesLoaded: false,
    rtpBankSources: [],
    selectedBankForFees: null,
    defaultPaymentMethod: null,
  }),
  getters: {
    banksTotalCount: (state): number => state.connectedBanksCount + useMicroDepositStore().microDepositList.total,
  },
  actions: {
    async fetchAction(isSignUp = false): Promise<FundingSource[]> {
      const { $snapApi } = useNuxtApp();
      const url: string = isSignUp ? 'sign-up/banks' : 'transfer/get-funding-sources';

      const { data }: { data: FundingSourceListDto } = await $snapApi({
        method: 'GET',
        url,
      });

      const { totalCount, fundingSources } = data;

      this.connectedBanksCount = totalCount;
      return fundingSources;
    },
    async fetchCheckfloProfileAction(isSignUp: boolean = false) {
      const { $snapApi } = useNuxtApp();

      const { data } = await $snapApi({
        method: 'get',
        url: isSignUp ? 'sign-up/checkflo/profile' : 'checkflo/getProfile',
      });

      await this.fetchDefaultPaymentMethod();

      this.checkfloProfile = data;
    },
    async fetchCheckfloSubmerchantsAction() {
      const { $snapApi } = useNuxtApp();
      const { data } = await $snapApi({
        method: 'get',
        url: 'checkflo/getSubmerchants',
      });

      this.checkfloSubmerchants = Array.isArray(data) ? data : [];
    },
    async fetchFundingSourcesWithCheckfloSubmerchants() {
      this.loading = true;

      try {
        const fundingSources = await this.fetchAction();
        try {
          await this.fetchCheckfloSubmerchantsAction();
        } catch {
          this.checkfloSubmerchants = [];
        }
        try {
          await this.fetchCheckfloProfileAction();
        } catch {
          this.checkfloProfile = { addresses: [], profileId: 0 } as CheckfloProfile;
        }
        this.setFundingSourcesData(fundingSources);
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async fetchFundingSourcesWithCheckfloProfile() {
      // Backward compatible alias while callers migrate to submerchant naming.
      await this.fetchFundingSourcesWithCheckfloSubmerchants();
    },
    async fetchDefaultPaymentMethod() {
      const { $snapApi } = useNuxtApp();
      const { data } = await $snapApi({ method: 'get', url: 'default-payment' });
      this.defaultPaymentMethod = data;
    },
    async fetch(isSignUp = false) {
      this.loading = true;

      try {
        const fundingSources = await this.fetchAction(isSignUp);
        this.setFundingSourcesData(fundingSources);
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async ensureFundingSourcesLoaded(force = false): Promise<void> {
      if (!force && this.isFundingSourcesLoaded) {
        return;
      }

      if (fundingSourcesRequest) {
        return fundingSourcesRequest;
      }

      fundingSourcesRequest = (async () => {
        this.loading = true;

        try {
          const fundingSources = await this.fetchAction();
          this.setFundingSourcesData(fundingSources);
        } finally {
          this.loading = false;
          fundingSourcesRequest = null;
        }
      })();

      return fundingSourcesRequest;
    },
    async fetchSystemFundingSource() {
      const { $snapApi } = useNuxtApp();
      const { data }: { data: FundingSourceListDto } = await $snapApi({
        method: 'get',
        url: 'transfer/get-funding-sources-system',
      });
      const { totalCount, fundingSources } = data;

      this.connectedBanksCount = totalCount;

      this.setFundingSourcesData(fundingSources);
    },
    setFundingSourcesData(fundingSources: FundingSource[]) {
      this.bankForFees = null;
      this.bankForPayments = null;
      this.bankSources = [];
      const balance = fundingSources.find((fs) => fs.type === 'balance');
      if (balance) {
        this.wallet = balance;
      }
      this.bankSources = fundingSources
        .filter((fs) => fs.type === 'bank')
        .map((fs) => {
          const bankSource = convertFundingToBankSource(fs);
          if (fs.forFees) {
            this.bankForFees = bankSource;
          }
          if (fs.forPayments) {
            this.bankForPayments = bankSource;
          }

          return bankSource;
        });
      this.isFundingSourcesLoaded = true;
    },
    async setFundingSourceForFees(bankSource: BankSource, isSameAsPaymentSource: boolean = false) {
      const { $snapApi, $toastSuccess } = useNuxtApp();
      this.loading = true;
      await $snapApi({
        method: 'post',
        url: `dwolla/funding-sources/fees?isSameAsPaymentSource=${isSameAsPaymentSource}`,
        data: { fundingSourceUrl: bankSource.fundingSourceUrl },
      });
      this.bankForFees = bankSource;
      this.loading = false;
      $toastSuccess('Set bank for fees successfully');
    },
    async unSetFundingSourceForFees() {
      const { $snapApi, $toastSuccess } = useNuxtApp();
      this.loading = true;
      await $snapApi({
        method: 'post',
        url: 'dwolla/funding-sources/unset',
      });
      this.loading = false;
      $toastSuccess('Unset bank for fees successfully');
    },
    async saveCheckfloProfile(profileId: number | undefined) {
      const { $snapApi } = useNuxtApp();

      await $snapApi({
        method: 'post',
        url: 'checkflo/saveProfile',
        data: {
          userid: profileId ?? this.checkfloProfile.profileId,
          data: this.checkfloProfile,
        },
      });
    },
    async fetchCheckfloProfile(isSignUp: boolean = false) {
      this.loading = true;

      try {
        await this.fetchCheckfloProfileAction(isSignUp);
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async addMerchant(registrationFormValues: RegistrationFormValues, profileId: number | undefined) {
      const { $snapApi } = useNuxtApp();

      registrationFormValues.federalTaxId = registrationFormValues.federalTaxId?.replace('-', '') ?? null;

      const data = {
        ...registrationFormValues,
        businessNumber: registrationFormValues.federalTaxId,
        user: profileId ?? this.checkfloProfile.profileId,
        countryCode: COUNTRY_CODE,
      };

      const response = await $snapApi({ method: 'post', url: 'checkflo/merchantAccount/RegisterAccount', data });

      this.checkfloProfile.merchantId = response.data.merchantAID;
    },
    async addBank(bankAdditionFormValues: BankAdditionFormValues, bank: BankSource) {
      const { $snapApi } = useNuxtApp();
      const { data: $auth } = useAuth();

      const data = {
        firstName: $auth.value?.firstName,
        lastName: $auth.value?.lastName,
        bankName: bank.bankName,
        bankNickname: bank.customName,
        accountType: bank.bankAccountType,
        inBehalf: this.checkfloProfile.merchantId,
        ...bankAdditionFormValues,
        printBankAddress: Number(bankAdditionFormValues.printBankAddress),
        printCompanyAddress: Number(bankAdditionFormValues.printCompanyAddress),
        printCompanyName: Number(bankAdditionFormValues.printCompanyName),
      };

      const bankId = bank.id.toString();

      const response = await $snapApi({ method: 'post', url: 'checkflo/merchantAccount/addBank', data });

      if (!this.checkfloProfile.banks || !this.checkfloProfile.bankStatus) {
        this.checkfloProfile.banks = { [bankId]: response.data.bankToken };
        this.checkfloProfile.bankStatus = { [bankId]: AccountStatus.Incomplete };
      } else {
        this.checkfloProfile.banks[bankId] = response.data.bankToken;
        this.checkfloProfile.bankStatus[bankId] = AccountStatus.Incomplete;
      }
    },
    async uploadLogo(bankId: string, image64: string, profileId: number | undefined) {
      const { $snapApi } = useNuxtApp();

      const bankToken = this.checkfloProfile.banks ? this.checkfloProfile.banks[bankId] : '';

      const data = {
        bankToken: bankToken,
        inBehalf: this.checkfloProfile.merchantId,
        logo: image64,
        userId: profileId ?? this.checkfloProfile.profileId,
      };

      await $snapApi({ method: 'post', url: 'checkflo/merchantAccount/uploadCompanyLogo', data });

      this.companyLogo = image64;
    },
    async uploadLogoByToken(bankToken: string, merchantId: string, image64: string, profileId: number | undefined) {
      const { $snapApi } = useNuxtApp();
      await $snapApi({
        method: 'post',
        url: 'checkflo/merchantAccount/uploadCompanyLogo',
        data: {
          bankToken,
          inBehalf: merchantId,
          logo: image64,
          userId: profileId ?? this.checkfloProfile.profileId,
        },
      });

      this.companyLogo = image64;
    },
    async saveCheckfloAddresses(addresses: AddressDto[]) {
      const { $snapApi, $toastSuccess, $toastError } = useNuxtApp();
      let saved = false;
      try {
        const { data: result }: { data: { saved: boolean } } = await $snapApi({
          method: 'post',
          url: CASH_OUT_ADDRESS_SAVE_ENDPOINT,
          data: { addresses },
        });
        if (result?.saved !== true) {
          throw new Error(CASH_OUT_ADDRESS_SAVE_RETRY_MESSAGE);
        }
        saved = true;
        const { data }: { data: CheckfloProfile } = await $snapApi({ method: 'get', url: 'checkflo/getProfile' });
        if (
          !Array.isArray(data?.addresses) ||
          data.addresses.length !== addresses.length ||
          data.addresses.some((address) => !Number.isInteger(address.id) || address.id <= 0)
        ) {
          throw new Error('Saved addresses could not be verified');
        }
        this.checkfloProfile = data;
        $toastSuccess('Address updated successfully');
      } catch (error) {
        const fieldError = readCashOutAddressFieldError(error);
        if (fieldError) {
          throw fieldError;
        }
        $toastError(
          saved
            ? 'Address saved, but could not be refreshed. Please reload this page.'
            : CASH_OUT_ADDRESS_SAVE_RETRY_MESSAGE,
        );
        throw error;
      }
    },
    async fetchMicroDeposit() {
      const { $snapApi } = useNuxtApp();
      const { data } = await $snapApi({ method: 'get', url: 'identity/get-micro-deposit' });

      this.microDeposit = data.data;
    },
    async fetchPlaidToken() {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        const { data } = await $snapApi({ method: 'get', url: 'identity/link-token' });

        this.publicPlaidToken = data;
      } finally {
        this.loading = false;
      }
    },
    async createPlaidAccessToken(data: { public_token: string; accounts: PlaidAccount[] }) {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({ method: 'post', url: 'identity/token', data });
      } finally {
        this.loading = false;
      }
    },
    async saveMicroDepositBank(data: { accounts: PlaidAccount }, forFees: boolean) {
      const { $snapApi } = useNuxtApp();

      await $snapApi({ method: 'post', url: `identity/add-micro-deposit?for_fees=${forFees}`, data });
    },
    async createFundingSource(forFees: boolean) {
      const { $snapApi } = useNuxtApp();

      await $snapApi({ method: 'get', url: `identity/bank?for_fees=${forFees}` });

      this.fundingFromSystem = false;
    },
    async getCustomerStatus(): Promise<CustomerStatus['status']> {
      const { $snapApi } = useNuxtApp();
      const { data }: { data: CustomerStatus } = await $snapApi({ method: 'post', url: 'customer-status' });

      return data.status;
    },
    async removeFundingSource(fundingSourceUrl: string) {
      const { $snapApi, $toastSuccess, $toastError } = useNuxtApp();
      const data = {
        fundingSourceURL: fundingSourceUrl,
      };

      try {
        await $snapApi({
          method: 'POST',
          url: 'remove-funding-source',
          data,
        });

        this.fundingFromSystem = false;
        $toastSuccess('🗑️ Removed successfully');
        this.bankForFees = null;
        await this.fetchFundingSourcesWithCheckfloSubmerchants();
      } catch {
        $toastError('Invalid funding source');
      }
    },
    async verifyFundingSource(microDepositFundingUrl: string, firstAmount: string, secondAmount: string) {
      this.loading = true;
      const { $snapApi, $toastSuccess } = useNuxtApp();
      const data = {
        fundingSourceURL: microDepositFundingUrl,
        amount: Number(firstAmount),
        amount2: Number(secondAmount),
      };

      try {
        await $snapApi({
          method: 'POST',
          url: 'verify-micro-deposit',
          data,
        });

        await this.saveDefaultSource(microDepositFundingUrl);
        $toastSuccess("☑️ Nice! We've verified your bank account");
      } finally {
        this.loading = false;
      }
    },
    async saveDefaultSource(microDepositFundingUrl: string) {
      const { $snapApi } = useNuxtApp();
      const data = {
        fundingSourceURL: microDepositFundingUrl,
      };

      try {
        await $snapApi({
          method: 'POST',
          url: 'save-default-source',
          data,
        });

        this.fundingFromSystem = false;
        await this.fetch();
      } catch {}
    },
    async saveDefaultFundingSource(bankSource: BankSource, isSameAsFeeSource: boolean = false) {
      const { $snapApi, $toastSuccess } = useNuxtApp();

      const data = {
        fundingSourceURL: bankSource.fundingSourceUrl,
      };

      this.loading = true;

      try {
        const url = `merchant/update-default-source?isSameAsFeeSource=${isSameAsFeeSource}`;
        await $snapApi({ method: 'post', url: url, data });
        $toastSuccess('Set default bank successfully');
        this.bankForPayments = bankSource;
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async deleteFundingSource(sourceUrl: string) {
      const { $snapApi, $toastSuccess } = useNuxtApp();

      const data = {
        fundingSourceURL: sourceUrl,
      };

      this.loading = true;

      try {
        await $snapApi({ method: 'post', url: 'remove-funding-source', data });

        this.fundingFromSystem = false;

        $toastSuccess('🗑️ Removed successfully');

        await this.fetchFundingSourcesWithCheckfloSubmerchants();
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async fetchRtpFundingSources() {
      const { $snapApi } = useNuxtApp();
      this.loading = true;
      const { data }: { data: FundingSourceListDto } = await $snapApi({
        method: 'get',
        url: 'dwolla/funding-sources/rtp',
      });
      this.loading = false;
      const { totalCount, fundingSources } = data;

      this.connectedBanksCount = totalCount;

      this.rtpBankSources = fundingSources
        .filter((fs) => fs.type === 'bank')
        .map((fs) => convertFundingToBankSource(fs));
    },
    async fetchLinkToken(): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        const response = await $snapApi({
          method: 'GET',
          url: 'sign-up/plaid/link-token',
        });

        this.publicPlaidToken = response.data;
      } finally {
        this.loading = false;
      }
    },
    async createToken(model: CreatePlaidTokenRequest): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;
      try {
        await $snapApi({
          method: 'POST',
          url: 'sign-up/plaid/create-token',
          data: model,
        });
      } finally {
        this.loading = false;
      }
    },
    async createBankWithMicroDeposit(data: { accounts: PlaidAccount }, forFees: boolean): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({
          method: 'post',
          url: `sign-up/banks/with-micro-deposit?for_fees=${forFees}`,
          data,
        });

        this.fundingFromSystem = false;
      } finally {
        this.loading = false;
      }
    },
    async createBankWithoutMicroDeposit(forFees: boolean): Promise<void> {
      const { $snapApi } = useNuxtApp();

      this.loading = true;

      try {
        await $snapApi({
          method: 'post',
          url: `sign-up/banks/without-micro-deposit?for_fees=${forFees}`,
        });

        this.fundingFromSystem = false;
      } finally {
        this.loading = false;
      }
    },
    async fetchBanks() {
      const { $snapApi } = useNuxtApp();
      const url: string = 'sign-up/banks';

      this.loading = true;

      try {
        const { data }: { data: FundingSourceListDto } = await $snapApi({
          method: 'GET',
          url,
        });

        const { totalCount, fundingSources } = data;

        this.connectedBanksCount = totalCount;

        this.setFundingSourcesData(fundingSources);
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async deleteBank(id: number): Promise<void> {
      const { $snapApi } = useNuxtApp();
      const url: string = `sign-up/banks/${id}`;

      this.loading = true;

      try {
        await $snapApi({
          method: 'POST',
          url,
        });
      } catch {
      } finally {
        this.loading = false;
      }
    },
    async setDefaultPaymentMethod(fundingSourceId: number, paymentType: string) {
      const { $snapApi, $toastSuccess } = useNuxtApp();
      try {
        await $snapApi({
          method: 'post',
          url: 'default-payment',
          data: { type: paymentType, fundingSourceId, addressId: null, shipping: null },
        });
        $toastSuccess('Default payment method set successfully');
      } catch (error) {
        $toastError('Failed to set default payment method');
        throw error;
      }
    },
    async updateBankNickname(fundingSourceId: number, nickname: string | null) {
      const { $snapApi, $toastSuccess } = useNuxtApp();
      await $snapApi({
        method: 'post',
        url: 'dwolla/funding-sources/nickname',
        data: { fundingSourceId, nickname },
      });

      const normalizedNickname = nickname?.trim() ?? '';
      this.bankSources = this.bankSources.map((bank) => {
        if (bank.id !== fundingSourceId) {
          return bank;
        }

        return {
          ...bank,
          customName: normalizedNickname.length > 0 ? normalizedNickname : bank.customName,
        };
      });
      if (this.bankForFees?.id === fundingSourceId) {
        this.bankForFees = {
          ...this.bankForFees,
          customName: normalizedNickname.length > 0 ? normalizedNickname : this.bankForFees.customName,
        };
      }
      if (this.bankForPayments?.id === fundingSourceId) {
        this.bankForPayments = {
          ...this.bankForPayments,
          customName: normalizedNickname.length > 0 ? normalizedNickname : this.bankForPayments.customName,
        };
      }

      $toastSuccess('Bank nickname updated successfully');
    },
    async setDefaultPaymentMethodForPaperCheck(addressId: number, shippingType: string) {
      try {
        const { $snapApi, $toastSuccess } = useNuxtApp();
        await $snapApi({
          method: 'post',
          url: 'default-payment',
          data: { type: 'Paper Check', fundingSourceId: null, addressId, shipping: shippingType },
        });
        $toastSuccess('Default payment method set successfully');
      } catch (error) {
        $toastError('Failed to set default payment method');
        throw error;
      }
    },
    async removeDefaultPaymentMethod() {
      try {
        const { $snapApi, $toastSuccess } = useNuxtApp();
        await $snapApi({ method: 'post', url: 'default-payment/remove' });
        $toastSuccess('Default payment method removed successfully');
      } catch (error) {
        $toastError('Failed to remove default payment method');
        throw error;
      }
    },
    async updateCheckfloBankShipping(
      checkfloBankId: number,
      options: { sendByFedex: boolean; sendByMail: boolean; sendByUSPSFirst: boolean; sendByUSPSTrack: boolean },
    ) {
      const { $snapApi, $toastSuccess, $toastError } = useNuxtApp();
      try {
        await $snapApi({
          method: 'POST',
          url: 'checkflo/bank/shipping',
          data: { checkfloBankId, ...options },
        });
        $toastSuccess('Shipping methods updated successfully');
      } catch (error) {
        $toastError('Failed to update shipping methods');
        throw error;
      }
    },
    async uploadBankLogo(
      checkfloBankId: number,
      bankToken: string,
      merchantId: string,
      logo: string,
      profileId: number,
    ) {
      const { $snapApi } = useNuxtApp();
      await $snapApi({
        method: 'POST',
        url: 'checkflo/bank/logo/upload',
        data: { checkfloBankId, logo },
      });

      $snapApi({
        method: 'POST',
        url: 'checkflo/merchantAccount/uploadCompanyLogo',
        data: { bankToken, inBehalf: merchantId, logo, userId: profileId },
      }).catch(() => {});
    },
    async removeBankLogo(checkfloBankId: number) {
      const { $snapApi } = useNuxtApp();
      await $snapApi({
        method: 'POST',
        url: 'checkflo/bank/logo/remove',
        data: { checkfloBankId },
      });
    },
    async setSubmerchantBrandingProfile(checkfloSubmerchantId: number, brandingProfileId: number | null) {
      const { $snapApi } = useNuxtApp();
      await $snapApi({
        method: 'POST',
        url: 'checkflo/submerchant/branding-profile',
        data: { checkfloSubmerchantId, brandingProfileId },
      });
    },
    async removeCheckfloBank() {
      const { $snapApi, $toastSuccess, $toastError } = useNuxtApp();
      try {
        await $snapApi({
          method: 'POST',
          url: 'checkflo/remove-bank-setup',
        });

        $toastSuccess('Paper Check setup removed successfully');
      } catch (error) {
        $toastError('Failed to remove Paper Check setup');
        throw error;
      }
    },
  },
});
